import { ref, reactive } from 'vue'

const devMode = ref(false)
const copiedText = ref('')
const copiedPos = reactive({ x: 0, y: 0 })
let copiedTimer = null

const hoveredEl = reactive({
  tag: '',
  classes: '',
  id: '',
  componentName: '',
  displayName: '',
  size: '',
  padding: '',
  margin: '',
  position: '',
  display: '',
  font: '',
  color: '',
  bg: '',
  rect: null,
  paddingBox: null,
  marginBox: null
})

function parsePixelValues(str) {
  return str.split(' ').map(v => parseFloat(v) || 0)
}

function getVueComponentName(el) {
  let node = el
  while (node) {
    if (node.__vueParentComponent) {
      const comp = node.__vueParentComponent
      const name = comp.type?.name || comp.type?.__name || ''
      if (name) return name
    }
    node = node.parentElement
  }
  return ''
}

function buildDisplayName(el, componentName) {
  const tag = el.tagName.toLowerCase()
  const id = el.id ? `#${el.id}` : ''
  const classes = el.className && typeof el.className === 'string'
    ? '.' + el.className.trim().split(/\s+/).slice(0, 2).join('.')
    : ''

  if (componentName) {
    return `<${componentName}> → ${tag}${id}${classes}`
  }
  return `${tag}${id}${classes}`
}

function handleMouseMove(e) {
  if (!devMode.value) return
  const el = document.elementFromPoint(e.clientX, e.clientY)
  if (!el || el.closest('.dev-mode-tooltip') || el.closest('.dev-mode-toggle') || el.closest('.dev-copied-toast') || el.closest('.dev-shortcut-badge')) return

  const cs = getComputedStyle(el)
  const rect = el.getBoundingClientRect()

  const pt = parsePixelValues(cs.paddingTop + ' ' + cs.paddingRight + ' ' + cs.paddingBottom + ' ' + cs.paddingLeft)
  const mt = parsePixelValues(cs.marginTop + ' ' + cs.marginRight + ' ' + cs.marginBottom + ' ' + cs.marginLeft)

  const componentName = getVueComponentName(el)

  hoveredEl.tag = el.tagName.toLowerCase()
  hoveredEl.classes = el.className && typeof el.className === 'string'
    ? '.' + el.className.trim().split(/\s+/).join('.')
    : ''
  hoveredEl.id = el.id ? '#' + el.id : ''
  hoveredEl.componentName = componentName
  hoveredEl.displayName = buildDisplayName(el, componentName)
  hoveredEl.size = `${Math.round(rect.width)} × ${Math.round(rect.height)}`
  hoveredEl.padding = cs.padding
  hoveredEl.margin = cs.margin
  hoveredEl.position = cs.position
  hoveredEl.display = cs.display
  hoveredEl.font = `${cs.fontSize} ${cs.fontFamily.split(',')[0]}`
  hoveredEl.color = cs.color
  hoveredEl.bg = cs.backgroundColor
  hoveredEl.rect = { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
  hoveredEl.paddingBox = pt
  hoveredEl.marginBox = mt
}

function handleClick(e) {
  if (!devMode.value) return
  const el = document.elementFromPoint(e.clientX, e.clientY)
  if (!el || el.closest('.dev-mode-tooltip') || el.closest('.dev-mode-toggle') || el.closest('.dev-copied-toast') || el.closest('.dev-shortcut-badge')) return

  e.preventDefault()
  e.stopPropagation()

  const componentName = getVueComponentName(el)
  const selector = buildDisplayName(el, componentName)

  navigator.clipboard.writeText(selector).then(() => {
    copiedText.value = selector
    copiedPos.x = e.clientX
    copiedPos.y = e.clientY
    clearTimeout(copiedTimer)
    copiedTimer = setTimeout(() => { copiedText.value = '' }, 1500)
  })
}

function toggleDevMode() {
  devMode.value = !devMode.value
  if (!devMode.value) {
    copiedText.value = ''
    hoveredEl.rect = null
  }
}

function handleKeydown(e) {
  if (e.ctrlKey && e.shiftKey && e.key === 'D') {
    e.preventDefault()
    toggleDevMode()
  }
}

export function useDevMode() {
  return { devMode, hoveredEl, copiedText, copiedPos, handleMouseMove, handleClick, toggleDevMode, handleKeydown }
}
